import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import config from 'config';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import { spaces } from '../../assets/styles/themes/tokens';
import { useSelector } from 'react-redux';
import { selectIsOnboardingCompleted, selectUser } from '../../reducers/user.selectors';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import useFetchDashboardSubjectsProducts from './hooks/useFetchDashboardSubjects';
import CoursesCarousel from '../../components/Dashboard/CoursesCarousel';
import SubjectCarousel from '../../components/Dashboard/SubjectCarousel';
import StudyflixCarousel from '../../components/Dashboard/StudyflixCarousel';
import ProfileSubjectRow from '../../components/Dashboard/ProfileSubjectRow/ProfileSubjectRow';
import ProfileStudyflixRow from '../../components/Dashboard/ProfileStudyflixRow/ProfileStudyflixRow';
import WorksheetRow from '../../components/Dashboard/WorksheetRow/WorksheetRow';
import PinnedDocumentsCarousel from '../../components/Dashboard/PinnedDocumentsCarousel/PinnedDocumentsCarousel';
import MukiActionCarousel from '../../components/Dashboard/MukiActionCarousel';
import { palette } from '../../assets/styles/themes/tokens';
import useRouter from '../../utils/useRouter';
import { routes } from '../../constants';
import { useAmplitudeExperiment } from '../../utils/ampli';
import useShouldShowAIAssistant from '../../utils/useShouldShowAIAssistant';
import { DashboardPromoBanner } from '../../components/PromoBanner';
import { mapSchoolStagesToClassYears } from '../../utils/profileFilters';

// Up-to-three profile subjects feed the treatment rows.
const MAX_PROFILE_SUBJECTS = 3;

const NewDashboardPage: React.FC = () => {
  const { router } = useRouter();
  const user = useSelector(selectUser);
  const isOnboardingCompleted = useSelector(selectIsOnboardingCompleted);

  // Shared `dashboard` experiment (key owned by PRO-5). Treatment replaces the
  // legacy per-subject carousels + old Studyflix carousel + Didaktik row with the
  // Muki action cards (PRO-5) and the profile-aware Studyflix + subject rows
  // (PRO-4). A treatment user who can't use Muki would get a near-empty new
  // dashboard, so gate on Muki-eligibility too and otherwise keep the legacy view.
  const { isReady, variant } = useAmplitudeExperiment(user, config.amplitudeExperiments.dashboard);
  const shouldShowAIAssistant = useShouldShowAIAssistant();
  const showNewDashboard = isReady && variant === 'treatment' && shouldShowAIAssistant;

  // On the new dashboard the profile-aware rows own their fetching — suppress the
  // legacy product fetches to avoid duplicate requests. Gate only on `!showNewDashboard`
  // (not `isReady`): control users are the majority, so we must not serialize their
  // subject rows behind the Amplitude round-trip (up to the 10s SDK timeout on a hard
  // load). The cost is that an eventual treatment user fires one legacy request burst
  // while the experiment is still resolving.
  const subjects = useFetchDashboardSubjectsProducts(!showNewDashboard);

  useEffect(() => {
    if (!isOnboardingCompleted) {
      router.replace(`/${routes.onboardingPage.root}?step=roles`);
    }
  }, [isOnboardingCompleted]);

  if (!user) return null;
  const hasSubjects = subjects.userSubjects.length !== 0;
  const initialSubjects = subjects.userSubjects.slice(0, 2);
  const remainingSubjects = subjects.userSubjects.slice(2);

  const topSubjects = subjects.userSubjects.slice(0, MAX_PROFILE_SUBJECTS);
  // The worksheet row spans every configured subject, not only the display top-three.
  const allSubjectIds = subjects.userSubjects.map((subject) => subject.id);
  const classYears = mapSchoolStagesToClassYears(user.schoolStages);
  const schoolType = user.schoolType;

  return (
    <MasterLayout forceNewNavigation>
      <Box
        sx={{
          backgroundColor: palette.background.paperElevation1,
          minHeight: '100vh'
        }}
      >
        <Box sx={{
          paddingTop: spaces.sm,
          paddingLeft: spaces.md,
          paddingBottom: spaces.xxxxl,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.lg,
        }}>
          <DashboardPageHeader firstName={user?.firstName} />
          <DashboardPromoBanner isEligible={showNewDashboard} />
          {showNewDashboard ? (
            <>
              <MukiActionCarousel />
              {hasSubjects && <WorksheetRow subjectIds={allSubjectIds} />}
              {hasSubjects && (
                <ProfileStudyflixRow
                  subjects={topSubjects}
                  classYears={classYears}
                  schoolType={schoolType}
                />
              )}
              {topSubjects.map(subject => (
                <ProfileSubjectRow
                  key={subject.id}
                  subject={subject}
                  classYears={classYears}
                  schoolType={schoolType}
                />
              ))}
            </>
          ) : (
            <>
              {
                hasSubjects &&
                  initialSubjects.map(subject => <SubjectCarousel key={subject.id} subject={subject} />)
              }
              <StudyflixCarousel />
              {
                remainingSubjects.length ?
                  remainingSubjects.map(subject => <SubjectCarousel key={subject.id} subject={subject} />)
                  : undefined
              }
              { subjects.defaultSubject && <SubjectCarousel subject={subjects.defaultSubject} /> }
              <PinnedDocumentsCarousel />
              <CoursesCarousel />
            </>
          )}
        </Box>
      </Box>
    </MasterLayout>
  );
};

export default NewDashboardPage;
